/**
 * Work Order Detail Page
 * Shows full work order information with:
 * - Department, Status, Assigned To
 * - Due date, Priority
 * - Parent Order link
 * - Checklist (editable by production)
 * - Instructions/notes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { operationsAPI } from '../../utils/api';
import { useRefetchOnReturn } from '../../hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
// Import shared components from the Unified Detail Page System
import { 
  DetailHeader, 
  DetailPageLayout, 
  ContentPanel, 
  StatusPill,
  STATUS_CONFIGS 
} from '../../components/detail-system';
import {
  Factory,
  User,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  ShoppingCart,
  FileText,
  Loader2,
  Flag,
  Play,
  Pause,
  CheckSquare,
  Square,
  Plus
} from 'lucide-react';

// Work order statuses (P1 Contract: simplified - no BLOCKED)
const WO_STATUSES = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700', icon: Square },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700', icon: Play },
  { value: 'done', label: 'Done', color: 'bg-green-100 text-green-700', icon: CheckCircle }
];

// Priorities
const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'med', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-700' }
];

// Departments with labels
const DEPARTMENTS = {
  art: { label: 'Art', icon: '🎨' },
  engraving: { label: 'Engraving', icon: '⚙️' },
  printing: { label: 'Printing', icon: '🖨️' },
  large_format: { label: 'Large Format', icon: '📐' },
  laser: { label: 'Laser', icon: '⚡' },
  shipping: { label: 'Shipping', icon: '📦' }
};

export default function WorkOrderDetailPage({ 
  workOrderId, 
  onBack, 
  onNavigateToOrder
}) {
  // Main data
  const [workOrder, setWorkOrder] = useState(null);
  const [parentOrder, setParentOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  
  // Form data
  const [selectedStatus, setSelectedStatus] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [instructions, setInstructions] = useState('');
  
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch work order details
  const fetchWorkOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await operationsAPI.getWorkOrder(workOrderId);
      setWorkOrder(data.work_order);
      setParentOrder(data.order);
      setSelectedStatus(data.work_order?.status || 'open');
      setInstructions(data.work_order?.instructions || '');
    } catch (err) {
      console.error('Failed to fetch work order:', err);
      setError('Failed to load work order details');
    } finally {
      setLoading(false);
    }
  }, [workOrderId]);

  // Initial data load
  useEffect(() => {
    if (workOrderId) {
      fetchWorkOrder();
    }
  }, [workOrderId, fetchWorkOrder]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchWorkOrder();
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchWorkOrder,
    enabled: !loading && !!workOrderId,
    refreshing,
    throttleMs: 1500,
  });

  // Handle status change
  const handleStatusChange = async () => {
    // P1 Contract: DONE is terminal - cannot change from done
    if (workOrder.status === 'done' && selectedStatus !== 'done') {
      alert('Cannot change status: DONE is terminal. Work order is complete.');
      return;
    }
    
    try {
      setSaving(true);
      // Use the new PATCH endpoint for status updates with validation
      await operationsAPI.updateWorkOrderStatus(workOrder.id, selectedStatus);
      setShowStatusModal(false);
      fetchWorkOrder();
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update status: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Handle checklist item toggle
  const handleToggleChecklistItem = async (index) => {
    const checklist = [...(workOrder.checklist || [])];
    checklist[index] = {
      ...checklist[index],
      completed: !checklist[index].completed,
      completed_at: !checklist[index].completed ? new Date().toISOString() : null
    };
    
    try {
      await operationsAPI.updateWorkOrder(workOrder.id, { checklist });
      fetchWorkOrder();
    } catch (err) {
      console.error('Failed to update checklist:', err);
    }
  };

  // Handle add checklist item
  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim()) return;
    
    const checklist = [...(workOrder.checklist || [])];
    checklist.push({
      text: newChecklistItem.trim(),
      completed: false,
      completed_at: null
    });
    
    try {
      setSaving(true);
      await operationsAPI.updateWorkOrder(workOrder.id, { checklist });
      setNewChecklistItem('');
      setShowChecklistModal(false);
      fetchWorkOrder();
    } catch (err) {
      console.error('Failed to add checklist item:', err);
      alert('Failed to add checklist item: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle save instructions
  const handleSaveInstructions = async () => {
    try {
      setSaving(true);
      await operationsAPI.updateWorkOrder(workOrder.id, { instructions });
      fetchWorkOrder();
    } catch (err) {
      console.error('Failed to save instructions:', err);
      alert('Failed to save instructions: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Get status info
  const getStatusInfo = (status) => {
    return WO_STATUSES.find(s => s.value === status) || WO_STATUSES[0];
  };

  // Get priority info
  const getPriorityInfo = (priority) => {
    return PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
  };

  // Get department info
  const getDepartmentInfo = (dept) => {
    return DEPARTMENTS[dept] || { label: dept, icon: '📋' };
  };

  // Check if overdue
  const isOverdue = workOrder?.status !== 'done' && workOrder?.due_at && new Date(workOrder.due_at) < new Date();

  // Calculate checklist progress
  const checklistProgress = workOrder?.checklist?.length > 0
    ? Math.round((workOrder.checklist.filter(c => c.completed).length / workOrder.checklist.length) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="work-order-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !workOrder) {
    return (
      <div className="flex flex-col items-center justify-center h-96" data-testid="work-order-detail-error">
        <p className="text-red-600 mb-4">{error || 'Work order not found'}</p>
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(workOrder.status);
  const priorityInfo = getPriorityInfo(workOrder.priority);
  const deptInfo = getDepartmentInfo(workOrder.department);

  // Work Order Status Config for DetailHeader
  const WO_STATUS_CONFIG = {
    open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
    in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
    done: { label: 'Done', color: 'bg-green-100 text-green-700' }
  };

  // Header Actions
  const headerPrimaryActions = [
    {
      label: 'Update Status',
      icon: Play,
      onClick: () => setShowStatusModal(true),
      testId: 'update-status-btn'
    }
  ];

  // Sidebar content
  const sidebarContent = (
    <>
      {/* Quick Status Update */}
      <ContentPanel title="Quick Status Update" icon={Play} testId="quick-status-panel">
        <div className="space-y-2">
          {WO_STATUSES.map((status) => {
            const StatusIcon = status.icon;
            const isActive = workOrder.status === status.value;
            const isDisabled = workOrder.status === 'done' && status.value !== 'done';
            return (
              <button
                key={status.value}
                disabled={isDisabled}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  isActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : isDisabled
                    ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={async () => {
                  if (status.value !== workOrder.status && !isDisabled) {
                    try {
                      await operationsAPI.updateWorkOrderStatus(workOrder.id, status.value);
                      fetchWorkOrder();
                    } catch (err) {
                      console.error('Failed to update status:', err);
                      alert('Failed to update status: ' + (err.message || 'Unknown error'));
                    }
                  }
                }}
                data-testid={`quick-status-${status.value}`}
              >
                <StatusIcon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className={isActive ? 'font-medium text-blue-700' : 'text-gray-700'}>
                  {status.label}
                </span>
                {isActive && <CheckCircle className="w-4 h-4 ml-auto text-blue-600" />}
              </button>
            );
          })}
          {workOrder.status === 'done' && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              Status is locked - DONE is terminal
            </p>
          )}
        </div>
      </ContentPanel>

      {/* Timeline */}
      <ContentPanel title="Timeline" icon={Clock} testId="timeline-panel">
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Created</span>
            <span>{new Date(workOrder.created_at).toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Updated</span>
            <span>{new Date(workOrder.updated_at).toLocaleString()}</span>
          </div>
          {workOrder.due_at && (
            <div className={`flex justify-between ${isOverdue ? 'text-red-600' : ''}`}>
              <span className={isOverdue ? 'text-red-600' : 'text-gray-500'}>
                {isOverdue ? 'Overdue Since' : 'Due'}
              </span>
              <span className="font-medium">
                {new Date(workOrder.due_at).toLocaleString()}
              </span>
            </div>
          )}
          {workOrder.completed_at && (
            <div className="flex justify-between text-green-600">
              <span>Completed</span>
              <span className="font-medium">
                {new Date(workOrder.completed_at).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </ContentPanel>
    </>
  );

  return (
    <>
    <DetailPageLayout
      headerProps={{
        entityName: workOrder.work_order_number,
        subtitle: `${deptInfo.icon} ${deptInfo.label} Department`,
        entityIcon: Factory,
        status: workOrder.status,
        statusConfig: WO_STATUS_CONFIG,
        statusEditable: true,
        onStatusClick: () => setShowStatusModal(true),
        onBack,
        backLabel: 'Back',
        onRefresh: handleRefresh,
        refreshing: refreshing,
        primaryActions: headerPrimaryActions,
        testId: 'work-order-detail-header'
      }}
      sidebarContent={sidebarContent}
      testId="work-order-detail-page"
    >
      {/* Work Order Overview */}
      <ContentPanel title="Work Order Details" icon={Factory} testId="work-order-details-panel">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 flex items-center gap-3">
            <StatusPill 
              status={workOrder.status}
              config={WO_STATUS_CONFIG}
              testId="wo-status-pill"
            />
            <Badge className={priorityInfo.color}>
              <Flag className="w-3 h-3 mr-1" />
              {priorityInfo.label} Priority
            </Badge>
            {isOverdue && (
              <Badge className="bg-red-100 text-red-700">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Overdue
              </Badge>
            )}
          </div>
          
          <div>
            <p className="text-xs text-gray-500 mb-1">Work Order #</p>
            <p className="text-sm font-medium text-gray-900">{workOrder.work_order_number}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Department</p>
            <p className="text-sm font-medium text-gray-900">{deptInfo.icon} {deptInfo.label}</p>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 mb-1">Assigned To</p>
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-gray-400" />
              <p className="text-sm text-gray-900">{workOrder.assigned_to_user_id || 'Unassigned'}</p>
            </div>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 mb-1">Due Date</p>
            <div className={`flex items-center gap-2 ${isOverdue ? 'text-red-600' : ''}`}>
              {isOverdue && <AlertTriangle className="w-4 h-4" />}
              <span className={`font-medium text-sm ${isOverdue ? 'text-red-600' : ''}`}>
                {workOrder.due_at 
                  ? new Date(workOrder.due_at).toLocaleDateString()
                  : 'Not set'}
              </span>
            </div>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 mb-1">Created</p>
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              <p className="text-sm text-gray-900">{new Date(workOrder.created_at).toLocaleString()}</p>
            </div>
          </div>
          
          {workOrder.completed_at && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Completed</p>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                <p className="text-sm text-gray-900">{new Date(workOrder.completed_at).toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      </ContentPanel>

      {/* Parent Order */}
      <ContentPanel title="Parent Order" icon={ShoppingCart} testId="parent-order-panel">
        {parentOrder ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-mono font-medium">{parentOrder.order_id}</p>
                <p className="text-sm text-gray-500">
                  {parentOrder.customer_name} • ${parentOrder.total?.toFixed(2)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={
                  parentOrder.status === 'in_production' ? 'bg-orange-100 text-orange-700' :
                  parentOrder.status === 'shipped' ? 'bg-green-100 text-green-700' :
                  'bg-blue-100 text-blue-700'
                }>
                  {parentOrder.status?.replace('_', ' ')}
                </Badge>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onNavigateToOrder?.(parentOrder.order_id)}
                  data-testid="view-parent-order-btn"
                >
                  View Order
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Order not found</p>
        )}
      </ContentPanel>

      {/* Checklist */}
      <ContentPanel 
        title="Checklist" 
        icon={CheckSquare}
        testId="checklist-panel"
        actions={
          <Button size="sm" variant="outline" onClick={() => setShowChecklistModal(true)} data-testid="add-checklist-btn">
            <Plus className="w-4 h-4 mr-1" />
            Add Item
          </Button>
        }
      >
        {workOrder.checklist && workOrder.checklist.length > 0 ? (
          <div className="space-y-3">
            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Progress</span>
                <span className="font-medium">{checklistProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${checklistProgress}%` }}
                />
              </div>
            </div>
            
            {/* Checklist items */}
            {workOrder.checklist.map((item, idx) => (
              <div 
                key={idx}
                className={`flex items-start gap-3 p-3 rounded-lg ${
                  item.completed ? 'bg-green-50' : 'bg-gray-50'
                }`}
                data-testid={`checklist-item-${idx}`}
              >
                <Checkbox
                  checked={item.completed}
                  onCheckedChange={() => handleToggleChecklistItem(idx)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className={item.completed ? 'line-through text-gray-500' : ''}>
                    {item.text}
                  </p>
                  {item.completed && item.completed_at && (
                    <p className="text-xs text-gray-400 mt-1">
                      Completed {new Date(item.completed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No checklist items</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setShowChecklistModal(true)}>
              Add Checklist Item
            </Button>
          </div>
        )}
      </ContentPanel>

      {/* Instructions */}
      <ContentPanel title="Instructions / Notes" icon={FileText} testId="instructions-panel">
        <div className="space-y-3">
          <Textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Add work instructions or notes..."
            rows={4}
            data-testid="instructions-textarea"
          />
          <div className="flex justify-end">
            <Button 
              size="sm" 
              onClick={handleSaveInstructions}
              disabled={saving || instructions === workOrder.instructions}
              data-testid="save-instructions-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Notes
            </Button>
          </div>
        </div>
      </ContentPanel>
    </DetailPageLayout>

    {/* Status Change Modal */}
    <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
      <DialogContent className="max-w-sm" data-testid="change-status-modal">
        <DialogHeader>
          <DialogTitle>Update Work Order Status</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <Label>New Status</Label>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WO_STATUSES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
          <Button onClick={handleStatusChange} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Update Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Add Checklist Item Modal */}
    <Dialog open={showChecklistModal} onOpenChange={setShowChecklistModal}>
      <DialogContent className="max-w-md" data-testid="add-checklist-modal">
        <DialogHeader>
          <DialogTitle>Add Checklist Item</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <Label>Item Description</Label>
          <Input
            value={newChecklistItem}
            onChange={(e) => setNewChecklistItem(e.target.value)}
            placeholder="Enter checklist item..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newChecklistItem.trim()) {
                handleAddChecklistItem();
              }
            }}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowChecklistModal(false)}>Cancel</Button>
          <Button onClick={handleAddChecklistItem} disabled={saving || !newChecklistItem.trim()}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Add Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
}
